import { useState, useMemo, useRef } from "react";
import { Box, Typography, Paper, CircularProgress } from "@mui/material";
import { colors } from "../ui/colors";
import { JobCard, CardDescription } from "./JobsWidgetWithWeek";
import { jobOverlapsDay } from "./utils/jobOverlapsRange";
import { useWidgetVariant } from "./useWidgetVariant";
import { TodayJobsCalendar, TodayJobsCalendarHandle } from "./TodayJobsCalendar";
import { RiveByKey } from "@components/riveAnimation";

const Dayjobs = ({ filter = "all", period = "today", jobs = [], isLoading = false, selectedDate = new Date(), timeZone }) => {
  const [open, setOpen] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState("");
  const timelineRef = useRef<TodayJobsCalendarHandle | null>(null);

  const dailyJobs = useMemo(() => {
    const currentFilter = filter.toLowerCase();

    return jobs.filter((job) => {
      const matchesDate = jobOverlapsDay(job, selectedDate, timeZone);
      const matchesType = currentFilter === "all" || job.type?.toLowerCase() === currentFilter;

      return matchesDate && matchesType;
    });
  }, [selectedDate, jobs, filter, timeZone]);

  const selectedJob = useMemo(() => dailyJobs.find((job) => String(job.id) === String(selectedJobId)), [selectedJobId, dailyJobs]);

  const activeProgress = useMemo(() => {
    const progress = selectedJob?.progress ?? 0;

    if (progress === 100) return 2;
    if (progress >= 66) return 1;

    return 0;
  }, [selectedJob]);

  const handleOpen = (id: string) => {
    const clickedJob = dailyJobs.find((job) => String(job.id) === String(id));
    const targetDate = clickedJob?.startDate || clickedJob?.date;

    if (targetDate) {
      timelineRef.current?.scrollToDate(targetDate);
    }

    if (selectedJobId === id && open) {
      handleClose();
      return;
    }

    setSelectedJobId(id);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedJobId("");
  };

  const { ref, isLargeSlot } = useWidgetVariant(500);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", p: 5 }}>
        <CircularProgress size={24} sx={{ color: colors.orange.base }} />
      </Box>
    );
  }

  return (
    <Box
      ref={ref}
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        maxHeight: isLargeSlot ? "100vh" : "330px",
        margin: "auto",
        backgroundColor: "var(--fx-surface-base, #FFFFFF)",
      }}
    >
      <Box sx={{ height: isLargeSlot ? "50%" : "40%", backgroundColor: "var(--fx-surface-base, #FFFFFF)" }}>
        <TodayJobsCalendar
          ref={timelineRef}
          currentDay={selectedDate}
          filter={filter}
          jobs={jobs}
          isLoading={isLoading}
          highlightedJobId={open ? selectedJobId : null}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 1, height: "55%", marginTop: isLargeSlot ? "0px" : "40px" }}>
        <Box
          sx={{
            flex: isLargeSlot ? (open ? 2 : 1) : open ? 0 : 1,
            backgroundColor: "var(--fx-surface-base, #FFFFFF)",
            overflowY: "auto",
            transition: "all 0.4s ease",
            minHeight: isLargeSlot ? "100%" : "24vh",
            colorScheme: "light",
            "&::-webkit-scrollbar": {
              width: "12px",
              display: "block",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(29, 28, 28, 0.5)",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "var(--fx-surface-muted, #F1F5F9)",
            },
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(42, 42, 42, 0.5) #f1f1f1",
          }}
        >
          <Typography
            sx={{
              color: colors.orange.base,
              fontWeight: 600,
              fontSize: "0.8rem",
              lineHeight: 1.2,
              my: 0.5,
            }}
          >
            {dailyJobs.length === 0 ? "No tasks scheduled" : `You have ${dailyJobs.length} ${dailyJobs.length === 1 ? "task" : "tasks"}`}
          </Typography>
          {dailyJobs.length > 0 ? (
            dailyJobs.map((job) => <JobCard key={job.id} {...job} onOpen={handleOpen} isSelected={selectedJobId === job.id} />)
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "50%",
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  borderRadius: "24px",
                  border: `1px solid rgba(255, 152, 0, 0.15)`,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  width: "auto",
                  px: 1.5,
                  pb: 1.5,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    overflow: "hidden",
                  }}
                >
                  <RiveByKey riveKey="createjob" boxSize={190} />
                </Box>

                <Typography
                  variant="h6"
                  sx={{
                    color: colors.orange.base,
                    fontWeight: 800,
                    fontSize: "1rem",
                    textAlign: "center",
                    marginTop: -4,
                  }}
                >
                  Your day is wide open
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    textAlign: "center",
                    fontSize: "0.85rem",
                    opacity: 0.8,
                  }}
                >
                  There are no {filter !== "all" ? filter : ""} jobs scheduled for this day.
                </Typography>
              </Paper>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            flex: isLargeSlot ? (open ? 1 : 0) : open ? 1 : 0,
            opacity: open ? 1 : 0,
            overflow: "hidden",
            transition: "all 0.4s ease",
            backgroundColor: "var(--fx-surface-base, #FFFFFF)",
            borderLeft: open ? "1px solid var(--fx-surface-border, #E2E8F0)" : "none",
            maxHeight: isLargeSlot ? "100vh" : "350px",
            mt: !isLargeSlot && -2,
          }}
        >
          {open && selectedJob && (
            <CardDescription
              job={selectedJob}
              nameJob={selectedJob.title}
              idJob={selectedJob.serviceOrderId || selectedJob.jobId || selectedJob.id}
              statusJob={selectedJob.status}
              type={selectedJob.type}
              activeProgress={activeProgress}
              onClose={handleClose}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Dayjobs;
